import { api } from "@common/api";
import { getDeploymentConfig } from "@common/deployment-config";
import { loggedFetch } from "@jkast90/kast-common/api-client";
import type { SaveMeta } from "../types/api";

// A save state is a few hundred KB to a few MB through the API, so its
// bytes get longer than a JSON call's 8 s.
const SAVE_BYTES = { timeoutMs: 60_000 };

// play-api's save routes:
//   GET    /saves                 -> CloudSaveMeta[]
//   GET    /saves/{name}          -> application/octet-stream
//   PUT    /saves/{name}          <- application/octet-stream
//   DELETE /saves/{name}
// Names are mGBA's own (<base>.sav, <base>.ss1..3); saves are scoped to the
// caller's sub server-side.

export type CloudSaveMeta = SaveMeta;

/**
 * The account's cloud save names with the token it is given (at load it may
 * still be in the URL hash), or null when the cloud can't say.
 */
export async function cloudSaveNames(token: string): Promise<Set<string> | null> {
  try {
    const response = await loggedFetch(
      `${getDeploymentConfig().apiUrl}/saves`,
      { headers: { Authorization: `Bearer ${token}` } },
      { timeoutMs: 4_000, logUrls: "url" },
    );
    if (!response.ok) return null;
    return new Set(((await response.json()) as CloudSaveMeta[]).map((s) => s.name));
  } catch {
    return null;
  }
}

export const cloudSaves = {
  // `signal` cancels the request: leaving the player gives up on a slow sync.
  async list(signal?: AbortSignal): Promise<CloudSaveMeta[]> {
    return api.get("/saves", { signal });
  },

  async upload(name: string, bytes: Uint8Array, signal?: AbortSignal): Promise<void> {
    await api.put("/saves/{name}", {
      params: { name },
      body: bytes,
      headers: { "Content-Type": "application/octet-stream" },
      signal,
      ...SAVE_BYTES,
    });
  },

  async download(name: string, signal?: AbortSignal): Promise<Uint8Array> {
    const bytes = await api.get("/saves/{name}", { params: { name }, signal, parseAs: "arrayBuffer", ...SAVE_BYTES });
    return new Uint8Array(bytes);
  },

  async remove(name: string, signal?: AbortSignal): Promise<void> {
    await api.delete("/saves/{name}", { params: { name }, signal });
  },
};
